/* A conversation in the reader (step 7): its messages stacked oldest first,
 * the latest open. Each is a native <details>, so it opens from the keyboard
 * with nothing added, and a message is DECODED ONLY WHEN IT IS OPENED: a
 * conversation of two hundred messages costs what the reader looks at.
 */

import { shortDate, count } from "./format.js";
import { setMarkedText } from "./highlight.js";

/**
 * @param {object} page          templates: conversation, item
 * @param {object} mailbox
 * @param {object} conversation  from `Mailbox.conversationOf`
 * @param {(id, body) => Promise<{framed}>} render  draws one message into a body element
 * @param {object} actions       back(), pdf(ids)
 */
export function conversationView(page, mailbox, conversation, render, actions) {
  const view = page.conversation.content.firstElementChild.cloneNode(true);
  const find = (name) => view.querySelector(`[data-mb="${name}"]`);

  setMarkedText(find("conv-subject"), conversation.subject || "(no subject)", mailbox.query.terms);
  find("conv-count").textContent = `${count(conversation.count)} messages`;
  find("conv-to-list").addEventListener("click", actions.back);
  find("conv-pdf").addEventListener("click", () => actions.pdf(conversation.members));

  const list = find("conv-messages");
  const handles = [];

  for (const id of conversation.members) {
    const item = mailbox.items[id];
    const details = page.item.content.firstElementChild.cloneNode(true);
    const part = (name) => details.querySelector(`[data-mb="${name}"]`);

    details.dataset.id = id;
    part("conv-from").textContent = item.fromName || item.fromAddress || "Unknown sender";
    part("conv-date").textContent = shortDate(item.date);
    part("conv-attachment").classList.toggle("hidden", !item.maybeAttachments);

    let drawn = false;
    const draw = async () => {
      if (drawn) return;
      drawn = true;
      const handle = await render(id, part("conv-body"));
      if (handle) handles.push(handle);
    };

    details.addEventListener("toggle", () => {
      if (details.open) draw();
    });

    // The latest message is the one being read, and while searching, every
    // message that matched opens too, so the reader sees why the conversation
    // is in the list. The rest wait to be opened.
    if (id === conversation.latest || mailbox.matchesSearch(id)) {
      details.open = true;
      draw();
    }

    list.appendChild(details);
  }

  return {
    element: view,
    dispose() {
      for (const handle of handles) handle?.dispose?.();
    },
  };
}
